import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { getPublicPageTopPadding, getStandardTopPadding } from '@/lib/layout-utils';
import { useUser } from '@/contexts/UserContext';
import { 
  ChevronRight, 
  Lightbulb, 
  Pizza, 
  Clock, 
  RotateCcw, 
  Calendar,
  ShoppingCart,
  Apple,
  Heart,
  Utensils,
  Timer,
  Users
} from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const Tips = () => {
  const { isAuthenticated } = useUser();
  const topPadding = isAuthenticated ? getStandardTopPadding() : getPublicPageTopPadding();
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "7 Game-Changing Meal Planning Tips for Busy Families",
    "description": "Discover practical meal planning strategies that actually work for real families. From the 3-core dinner rule to embracing assembled meals, these tips will transform your weekly routine.",
    "author": {
      "@type": "Organization",
      "name": "forkast"
    },
    "publisher": {
      "@type": "Organization",
      "name": "forkast"
    },
    "datePublished": "2025-09-04",
    "dateModified": "2025-09-04"
  };

  const tips = [
    {
      icon: Utensils,
      color: "sage",
      title: "Pick 3 core dinners and build around them",
      description: "You don't need 7 unique meals. Reuse, remix, repeat.",
      details: "Start with three reliable crowd-pleasers. Maybe it's pasta night, chicken something, and a one-pot wonder. Build variations around these foundations and watch your stress levels plummet.",
      badge: "Foundation Rule"
    },
    {
      icon: Users,
      color: "terracotta", 
      title: "Add meals your kids can help make",
      description: "Involve them on \"Pizza Night\" or \"Taco Tuesday\" — fun + learning.",
      details: "Turn cooking into family bonding time. Kids love assembling their own pizzas or filling their own tacos. It's educational, entertaining, and gives you tiny sous chefs.",
      badge: "Family Fun"
    },
    {
      icon: RotateCcw,
      color: "golden",
      title: "Rotate family favorites",
      description: "Keep a list of 10 hits and reuse them every 2–3 weeks.",
      details: "Create your \"greatest hits\" playlist of meals everyone actually eats. Rotate them regularly enough that they feel fresh but familiar. No more dinner decision fatigue.",
      badge: "Smart Strategy"
    },
    {
      icon: Calendar,
      color: "sunset",
      title: "Sync your plan with your week's schedule",
      description: "Busy days = faster meals. Plan accordingly.",
      details: "Soccer practice on Tuesday? That's a slow cooker day. Date night Friday? Perfect time for the kids' favorite mac and cheese. Match your meals to your real life, not your Pinterest dreams.",
      badge: "Reality Check"
    },
    {
      icon: ShoppingCart,
      color: "sage",
      title: "Batch your grocery list by category",
      description: "forkast does that for you. Makes shopping faster and less stressful.",
      details: "No more zigzagging through the store like a caffeinated pinball. Organize by produce, dairy, pantry staples. Your future self will thank you at checkout.",
      badge: "Efficiency Hack"
    },
    {
      icon: Apple,
      color: "terracotta",
      title: "Embrace \"assembled meals\"",
      description: "Cheese, bread, veggies. Dinner doesn't have to be cooked. Use forkast quick recipes.",
      details: "Sometimes the best dinner is a beautiful charcuterie board, fresh fruit, good bread, and cheese. Not every meal needs to involve turning on the stove. Give yourself permission to keep it simple.",
      badge: "Permission Granted"
    },
    {
      icon: Heart,
      color: "golden",
      title: "Meal planning is a kindness to your future self",
      description: "Start small, stay flexible, and don't aim for perfection.",
      details: "Every Sunday you that plans is giving a gift to every weekday you that's tired and hungry. Start with just planning 3 days ahead. Perfection is the enemy of progress (and dinner).",
      badge: "Self Care"
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${topPadding} pb-20 md:pb-0`}>
      <SEOHead 
        title="7 Game-Changing Meal Planning Tips for Busy Families | forkast" 
        description="Discover practical meal planning strategies that actually work for real families. From the 3-core dinner rule to embracing assembled meals, these tips will transform your weekly routine and reduce dinner stress." 
        jsonLd={jsonLd} 
      />
      
      {/* Hero Section */}
      <section className="py-4 md:py-12 bg-gradient-to-br from-background via-cream/20 to-sage/10">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12 md:mb-16 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Lightbulb className="h-8 w-8 text-golden-dark" />
              <Badge variant="secondary" className="bg-golden/20 text-golden-dark font-inter font-medium">
                Real Talk
              </Badge>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold font-playfair text-charcoal mb-6">
              7 Game-Changing Meal Planning Tips
            </h1>
            <p className="text-xl text-muted-foreground font-inter mb-8 max-w-3xl mx-auto">
              Forget Pinterest-perfect meal plans. These are the strategies that actually work for real families 
              with real schedules (and real picky eaters).
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                  Start meal planning now
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/how-forkast-meal-planning-works">
                <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                  See how forkast works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-4 md:py-16 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              const colorClasses = {
                sage: {
                  border: "border-sage/20",
                  bg: "bg-sage/20", 
                  bgMain: "bg-sage",
                  text: "text-sage"
                },
                terracotta: {
                  border: "border-terracotta/20",
                  bg: "bg-terracotta/20",
                  bgMain: "bg-terracotta", 
                  text: "text-terracotta"
                },
                golden: {
                  border: "border-golden/20",
                  bg: "bg-golden/20",
                  bgMain: "bg-golden",
                  text: "text-golden-dark"
                },
                sunset: {
                  border: "border-sunset/20", 
                  bg: "bg-sunset/20",
                  bgMain: "bg-sunset",
                  text: "text-sunset"
                }
              };
              
              const colors = colorClasses[tip.color as keyof typeof colorClasses];
              
              return (
                <article 
                  key={index}
                  className={`bg-background/80 rounded-2xl p-6 md:p-8 ${colors.border} shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`flex-shrink-0 w-12 h-12 ${colors.bgMain} text-cream rounded-xl flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge 
                          variant="secondary" 
                          className={`${colors.bg} ${colors.text} font-inter font-medium`}
                        >
                          {tip.badge}
                        </Badge>
                      </div>
                      
                      <h2 className="text-2xl font-bold font-playfair text-charcoal mb-3">
                        {tip.title}
                      </h2>
                      
                      <p className="text-lg text-charcoal font-inter font-medium mb-4">
                        {tip.description}
                      </p>
                      
                      <p className="text-muted-foreground font-inter leading-relaxed">
                        {tip.details}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-4 md:py-12 bg-gradient-to-br from-background via-cream/20 to-sunset/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-6">
              Put these tips into action
            </h2>
            <p className="text-xl text-muted-foreground font-inter mb-8 leading-relaxed">
              forkast makes implementing these strategies effortless. Build your recipe collection, 
              plan with your family, and generate smart shopping lists — all in one place.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                  Signup for free
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/how-forkast-meal-planning-works">
                <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                  How it works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tips;